import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/auth';
import { TrendingUp, ShoppingBag, Package, AlertTriangle } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';

interface TodayStats {
  date: string;
  revenue: number;
  orders: number;
  avgOrderValue: number;
  pendingOrders: number;
}

interface Snapshot {
  date: string;
  revenue: number;
  orders: number;
}

export default function DashboardPage() {
  const [today,     setToday]     = useState<TodayStats | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [lowStock,  setLowStock]  = useState<unknown[]>([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [todayRes, summaryRes, stockRes] = await Promise.all([
          apiFetch<{ data: TodayStats }>('/analytics/today'),
          apiFetch<{ data: { snapshots: Snapshot[] } }>('/analytics/summary?from=' + daysAgo(30)),
          apiFetch<{ data: unknown[] }>('/inventory/low-stock'),
        ]);
        setToday(todayRes.data);
        setSnapshots(summaryRes.data.snapshots);
        setLowStock(stockRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <LoadingSkeleton />;

  const fmt = (n: number) => `₹${(n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Dashboard</h1>
      <p style={styles.pageSubtitle}>Today's overview — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>

      {/* KPI cards */}
      <div style={styles.kpiGrid}>
        <KpiCard
          icon={<TrendingUp size={20} />}
          label="Revenue Today"
          value={fmt(today?.revenue ?? 0)}
          accent="var(--admin-accent)"
        />
        <KpiCard
          icon={<ShoppingBag size={20} />}
          label="Orders Today"
          value={String(today?.orders ?? 0)}
          sub={`${today?.pendingOrders ?? 0} pending`}
          accent="var(--admin-teal)"
        />
        <KpiCard
          icon={<Package size={20} />}
          label="Avg. Order Value"
          value={fmt(today?.avgOrderValue ?? 0)}
          accent="var(--admin-terracotta)"
        />
        <KpiCard
          icon={<AlertTriangle size={20} />}
          label="Low Stock SKUs"
          value={String(lowStock.length)}
          sub="Requires attention"
          accent={lowStock.length > 0 ? 'var(--admin-warning)' : 'var(--admin-success)'}
        />
      </div>

      {/* Revenue chart */}
      <div style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Revenue — Last 30 Days</h2>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={snapshots} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#C9A84C" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#C9A84C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--admin-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--admin-text-subtle)' }} tickLine={false} axisLine={false}
              tickFormatter={d => d.slice(5)} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: 'var(--admin-text-subtle)' }} tickLine={false} axisLine={false}
              tickFormatter={v => `₹${(v/100/1000).toFixed(0)}k`} width={50} />
            <Tooltip
              contentStyle={{ background: 'var(--admin-surface-3)', border: '1px solid var(--admin-border)', borderRadius: 6, fontSize: 12 }}
              formatter={(val: number) => [fmt(val), 'Revenue']}
              labelStyle={{ color: 'var(--admin-text-muted)' }}
            />
            <Area type="monotone" dataKey="revenue" stroke="#C9A84C" strokeWidth={2}
              fill="url(#revenueGrad)" dot={false} activeDot={{ r: 4, fill: '#C9A84C' }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Orders chart */}
      <div style={styles.chartCard}>
        <h2 style={styles.chartTitle}>Orders — Last 30 Days</h2>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={snapshots} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--admin-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--admin-text-subtle)' }} tickLine={false} axisLine={false}
              tickFormatter={d => d.slice(5)} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: 'var(--admin-text-subtle)' }} tickLine={false} axisLine={false} width={30} />
            <Tooltip
              contentStyle={{ background: 'var(--admin-surface-3)', border: '1px solid var(--admin-border)', borderRadius: 6, fontSize: 12 }}
              formatter={(val: number) => [val, 'Orders']}
            />
            <Line type="monotone" dataKey="orders" stroke="var(--admin-teal)" strokeWidth={2} dot={false}
              activeDot={{ r: 4, fill: 'var(--admin-teal)' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; accent: string;
}) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiIcon, color: accent, background: `${accent}1A` }}>{icon}</div>
      <div>
        <p style={styles.kpiLabel}>{label}</p>
        <p style={{ ...styles.kpiValue, color: accent }}>{value}</p>
        {sub && <p style={styles.kpiSub}>{sub}</p>}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ padding: 16 }}>
      <div style={{ height: 28, width: 200, background: 'var(--admin-surface-2)', borderRadius: 4, marginBottom: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginTop: 32 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ height: 100, background: 'var(--admin-surface-2)', borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );
}

function daysAgo(n: number) {
  return new Date(Date.now() - n * 86400000).toISOString().split('T')[0]!;
}

const styles: Record<string, React.CSSProperties> = {
  page:        { maxWidth: 1200, margin: '0 auto' },
  pageTitle:   { fontSize: 22, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 4 },
  pageSubtitle:{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 28 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 },
  kpiCard:     { background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 'var(--radius-lg)', padding: '20px 20px', display: 'flex', alignItems: 'flex-start', gap: 16 },
  kpiIcon:     { width: 40, height: 40, borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel:    { fontSize: 12, color: 'var(--admin-text-muted)', marginBottom: 4, fontWeight: 500 },
  kpiValue:    { fontSize: 22, fontWeight: 700, lineHeight: 1, marginBottom: 4 },
  kpiSub:      { fontSize: 11, color: 'var(--admin-text-subtle)' },
  chartCard:   { background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 },
  chartTitle:  { fontSize: 13, fontWeight: 600, color: 'var(--admin-text-muted)', marginBottom: 16, letterSpacing: '0.03em', textTransform: 'uppercase' as const },
};
